import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@base-ui/react/popover';

/** How long "Copied" stays before the control returns to its label. */
export const COPY_RESET_MS = 1600;
// A copy that takes longer than this (a fetch first) shows a spinner.
const PENDING_AFTER_MS = 150;

/**
 * Every copy control's states: idle, pending (only when the text is slow to
 * arrive), copied (for COPY_RESET_MS), and select, when the browser refuses
 * the clipboard and the text is offered to copy by hand (CopyFallback).
 * `copy` takes the text, or a function that returns it (or a promise of it).
 * `done` is what the control says once copied, e.g. "Prompt copied".
 */
export function useCopy() {
  const [state, setState] = useState('idle');
  const [text, setText] = useState('');
  const [done, setDone] = useState('');
  const timers = useRef({});
  const clear = () => {
    window.clearTimeout(timers.current.pending);
    window.clearTimeout(timers.current.reset);
  };
  useEffect(() => clear, []);

  const copy = async (source, { done: doneLabel = '' } = {}) => {
    clear();
    let value;
    if (typeof source === 'function') {
      timers.current.pending = window.setTimeout(() => setState('pending'), PENDING_AFTER_MS);
      try {
        value = await source();
      } catch {
        clear();
        setState('idle');
        return false;
      }
      window.clearTimeout(timers.current.pending);
    } else {
      value = source;
    }
    setDone(doneLabel);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(value);
      setText('');
      setState('copied');
      timers.current.reset = window.setTimeout(() => setState('idle'), COPY_RESET_MS);
      return true;
    } catch {
      setText(value);
      setState('select');
      return false;
    }
  };
  const dismiss = () => setState((current) => (current === 'select' ? 'idle' : current));
  return { state, text, done, copy, dismiss };
}

/**
 * Says the result to screen readers. A control's own label changes too, but
 * a focused button's new name is not announced everywhere.
 */
export function CopyStatus({ copy, children = 'Copied' }) {
  return (
    <span className="edl-sr-only" role="status">
      {copy.state === 'copied' ? copy.done || children : ''}
    </span>
  );
}

/**
 * When the clipboard is refused: the text in a small popover beside the
 * control, already selected, to copy by hand. Escape or a click outside
 * closes it. `anchor` is a ref to the element it points at.
 */
export function CopyFallback({ copy, anchor, label, multiline = false }) {
  const field = useRef(null);
  const open = copy.state === 'select';
  return (
    <Popover.Root open={open} onOpenChange={(next) => !next && copy.dismiss()}>
      <Popover.Portal>
        <Popover.Positioner className="edl-copy-pop__positioner" anchor={anchor} side="bottom" align="end" sideOffset={6}>
          <Popover.Popup className="edl-copy-pop" initialFocus={field}>
            <Popover.Title className="edl-copy-pop__title">Copy {label} with {isMac() ? '⌘C' : 'Ctrl+C'}</Popover.Title>
            {multiline ? (
              <textarea
                ref={field}
                className="edl-copy-pop__field edl-copy-pop__field--long"
                aria-label={label}
                readOnly
                value={copy.text}
                onFocus={(event) => event.currentTarget.select()}
              />
            ) : (
              <input
                ref={field}
                className="edl-copy-pop__field"
                aria-label={label}
                readOnly
                value={copy.text}
                onFocus={(event) => event.currentTarget.select()}
              />
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

function isMac() {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}
